import {
  ChevronsLeft,
  ChevronsRight,
  Menu,
  Search,
} from 'lucide-react';

import { ThemeToggle } from '@/components/theme/ThemeToggle';
import { Button } from '@/components/ui/button';
import { Kbd } from '@/components/ui/kbd';
import { Separator } from '@/components/ui/separator';
import { useSidebar } from '@/components/ui/sidebar';
import { NotificationBell } from '@/features/shared/components/NotificationBell';

import { Breadcrumbs } from './Breadcrumbs';
import { CommandPalette, useCommandPalette } from './CommandPalette';
import { UserMenu } from './UserMenu';

/**
 * Topbar dentro del SidebarInset.
 *
 * Desktop:
 *   [SidebarTrigger ‹/›] [|] [Breadcrumbs]
 *     ………………………………………… [ ⌘K · Buscar o ir a… ] [theme] [bell] [|] [user]
 *
 * Mobile (< 768px):
 *   [☰ Menu] ………………………………………… [🔍] [theme] [bell] [user]
 *   - Sidebar trigger se vuelve hamburguesa (abre Sheet).
 *   - Search pill colapsa a botón cuadrado de 36×36.
 *   - Breadcrumbs se ocultan (el sidebar drawer ya tiene la jerarquía).
 *   - Gap más chico para que los íconos no se pisen.
 *
 * Atajos globales: ⌘K command palette · T toggle tema · 1-9 nav directa.
 * Manejados por useCommandPalette().
 */
export function Topbar() {
  const { open, setOpen } = useCommandPalette();
  const { state, toggleSidebar, isMobile } = useSidebar();

  const sidebarCollapsed = state === 'collapsed';
  // En mobile usamos hamburguesa porque el sidebar es drawer/Sheet.
  // En desktop usamos chevrons que indican estado collapsed/expanded.
  const SidebarIcon = isMobile
    ? Menu
    : sidebarCollapsed
      ? ChevronsRight
      : ChevronsLeft;

  return (
    <>
      <header className="dd-bar-glass sticky top-0 z-30 flex h-[var(--topbar-h)] items-center gap-1.5 border-b px-2 sm:gap-2 sm:px-3 lg:px-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleSidebar}
          aria-label={
            isMobile
              ? 'Abrir menú'
              : sidebarCollapsed
                ? 'Expandir sidebar'
                : 'Contraer sidebar'
          }
          title={
            isMobile
              ? 'Menú'
              : sidebarCollapsed
                ? 'Expandir sidebar (⌘B)'
                : 'Contraer sidebar (⌘B)'
          }
          className="text-fg-muted hover:text-fg h-9 w-9 shrink-0"
        >
          <SidebarIcon className="h-4 w-4" />
        </Button>

        {/* Breadcrumbs — desktop only. */}
        <div className="hidden min-w-0 items-center gap-2 md:flex">
          <Breadcrumbs />
        </div>

        <div className="flex-1" />

        {/* Command palette trigger — pill ancho en desktop, ícono cuadrado en mobile */}
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="bg-surface-lo border-border text-fg-muted hover:bg-surface-hi inline-flex h-9 shrink-0 items-center justify-center gap-3 rounded-md border transition-colors md:min-w-[220px] md:justify-between md:px-2.5 w-9 md:w-auto"
          aria-label="Buscar (⌘K)"
          title="Buscar (⌘K)"
        >
          <span className="inline-flex items-center gap-2">
            <Search className="h-3.5 w-3.5" />
            <span className="hidden text-body md:inline">
              Buscar o ir a…
            </span>
          </span>
          <span className="hidden gap-0.5 md:inline-flex">
            <Kbd>⌘</Kbd>
            <Kbd>K</Kbd>
          </span>
        </button>

        <ThemeToggle />

        <NotificationBell />

        {/* Separator solo en desktop — en mobile gastaría espacio. */}
        <Separator orientation="vertical" className="mx-1 hidden h-6 sm:block" />
        <UserMenu />
      </header>

      <CommandPalette open={open} onOpenChange={setOpen} />
    </>
  );
}
